import React, { useEffect, useState } from "react";
import { FEATURES_LEFT, FEATURES_RIGHT, IMG } from "../data/mock";

// icon key -> real white glyph PNG (recovered from the original app)
const ICONS = {
  camera: "/assets/img/icons/camera.png",
  bible: "/assets/img/icons/bible.png",
  pen: "/assets/img/icons/pen.png",
  mic: "/assets/img/icons/mic.png",
  smile: "/assets/img/icons/smile.png",
  plus: "/assets/img/icons/plus.png",
};

function IconCircle({ name }) {
  // Shrink all glyphs ~30% except the pencil, which stays at full size.
  const size = name === "pen" ? "w-[42px] h-[42px]" : "w-[29px] h-[29px]";
  return (
    <div className="feat-icon flex items-center justify-center">
      <img
        src={ICONS[name]}
        alt=""
        className={`${size} object-contain`}
        draggable="false"
      />
    </div>
  );
}

function FeatureBox({ item, align }) {
  return (
    <div className={`feat-box ${align}`}>
      <IconCircle name={item.icon} />
      <div className="content">
        <p
          className={`font-serif-body font-bold text-[17px] ${
            item.accent ? "text-[#68c2d5]" : "text-[#333]"
          }`}
        >
          {item.title}
        </p>
        {/* Animated connector line + dot pointing toward the phone.
            The "plus" (share) box uses an L-shaped connector that turns 90°
            down to the bottom-center of the phone. */}
        {item.icon === "plus" ? (
          <span
            className="share-connector"
            style={{ "--dot-url": `url(${IMG.dot})` }}
          >
            <span className="share-h" />
            <span className="share-v" />
          </span>
        ) : (
          <span
            className="feat-dot"
            style={{ "--dot-url": `url(${IMG.dot})` }}
          />
        )}
        <p className="font-serif-body text-[#666] text-[14px] mt-2 leading-relaxed">
          {item.body}
        </p>
      </div>
    </div>
  );
}

export default function Features() {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    // Keep only the phone visible until the user actually starts scrolling;
    // then gracefully reveal the header + explainers and push it all down.
    const onScroll = () => {
      if (window.scrollY > 60) {
        setRevealed(true);
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const CALM = "ease-[cubic-bezier(0.16,1,0.3,1)]";
  const textReveal = (extra) =>
    `transition-all duration-[2000ms] ${CALM} ${
      revealed ? "opacity-100 translate-x-0 translate-y-0" : `opacity-0 ${extra}`
    }`;

  return (
    <section
      id="features"
      className={`pb-16 md:pb-24 transition-[padding] duration-[2000ms] ${CALM} ${
        revealed ? "pt-10 md:pt-16" : "pt-1 md:pt-2"
      }`}
    >
      {/* Header collapses out of flow while hidden so the phone sits close to
          the button, then gracefully expands as it reveals. */}
      <div
        className={`max-w-4xl mx-auto px-5 text-center overflow-hidden transition-all duration-[2000ms] ${CALM} ${
          revealed
            ? "max-h-[260px] opacity-100 translate-y-0"
            : "max-h-0 opacity-0 -translate-y-3"
        }`}
      >
        <h3 className="font-serif-display text-[#68c2d5] text-2xl md:text-[32px] font-bold">
          Different Ways To Share Your #LifeWithAbba
        </h3>
        <p className="font-serif-body text-[#666] text-[15px] md:text-[17px] mt-3">
          Many of the features in modern social platforms are here, but only to
          be used between you and <strong className="text-[#68c2d5]">Abba</strong>!
        </p>
      </div>

      <div className={`max-w-6xl mx-auto px-5 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6 items-center transition-[margin] duration-[2000ms] ${CALM} ${revealed ? "mt-8 md:mt-12" : "mt-4 md:mt-6"}`}>
        <div className={`flex flex-col gap-10 order-2 md:order-1 relative z-40 ${textReveal("-translate-x-6")}`}>
          {FEATURES_LEFT.map((f) => (
            <FeatureBox key={f.title} item={f} align="left" />
          ))}
        </div>

        <div className="flex justify-center order-1 md:order-2 relative z-0">
          <div
            aria-hidden="true"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[360px] h-[360px] md:w-[480px] md:h-[480px] rounded-full bg-[#e6f5f8] blur-3xl -z-10"
          />
          <img
            src={IMG.phone}
            alt="Life With Abba app"
            className="hero-phone max-h-[620px] w-auto"
          />
        </div>

        <div className={`flex flex-col gap-10 order-3 relative z-40 ${textReveal("translate-x-6")}`}>
          {FEATURES_RIGHT.map((f) => (
            <FeatureBox key={f.title} item={f} align="right" />
          ))}
        </div>
      </div>
    </section>
  );
}
